import { CommunityPostOptionsModel, deleteCommunityPost } from "client/Api/Communities/communityPosts";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { PostListData, PostListItem } from "./useCommunityPostListDataSource";
import {
	BusyIndicator,
	ConditionalWrapper,
	ConfirmationDialogInitialData,
	LoadMoreIndicator,
	NoResults,
	Portal,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { PostViewer } from "./PostViewer";
import styles from "./PostList.module.less";
import {
	CommunityPostDetailsViewModel,
	CommunityPostStatisticsModel
} from "client/Api/Communities/communityPostViewer";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone,
	useWarnBeforeUnload,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import {
	CommentListHandle,
	HubCommentSection,
	HubCommentSectionService
} from "../HubCommentSection/HubCommentSection";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { useImageViewer } from "../useImageViewer";
import {
	MobileComment,
	MobileCommentFormHandle,
	MobileCommentMode
} from "../HubCommentSection/MobileComment/MobileComment";
import { useMobileCommentState } from "../HubCommentSection/hooks/useMobileComment";
import { HubCommentModel } from "client/Api/Hub/common";
import { getCommunityCommentService } from "./getCommunityService";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { MobileCommentBoxPreservedSpace } from "../HubCommentSection/MobileComment/MobileCommentBoxPreservedSpace";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

type PostListProps = {
	options: CommunityPostOptionsModel;
	postListData: PostListData;
	onOpenPost: (postId: number, deeplink?: "comments") => void;
	onNavigateToCommunity?: (communityId: number) => void;
	onLoadMore: () => void;
	onReload: () => void;
	onScrollBehaviorChange?: (isScrollEnabled: boolean) => void;
	mobileCommentHostingLocation?: MobileCommentHostingLocation;
};

export const PostList = (props: PostListProps): ReactElement => {
	const {
		options,
		postListData,
		onNavigateToCommunity,
		onOpenPost,
		onLoadMore,
		onReload,
		onScrollBehaviorChange,
		mobileCommentHostingLocation
	} = props;

	const {
		state
	} = postListData;

	const {
		hasMore,
		items,
		status
	} = state;

	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);
	const [activePostId, setActivePostId] = useState<number | undefined>(undefined);
	const mobileCommentFormRef: RefObject<MobileCommentFormHandle | null> = useRef<MobileCommentFormHandle>(null);
	const commentListRefs: ReadonlyRefObject<Map<number, CommentListHandle | null>> = useRef<Map<number, CommentListHandle | null>>(new Map());

	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [imageViewerDialog, viewImageHandler] = useImageViewer();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	useWarnBeforeUnload(isEditingMobileComment);

	const activePostItem = useMemo(
		(): PostListItem | undefined => {
			if (isNil(activePostId)) {
				return undefined;
			}

			return items.find(
				x => x.id === activePostId
			);
		},
		[items, activePostId]
	);

	const currentUserInfo: UserIdentityModel | null = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	const setCommentListRef = useCallback(
		(postId: number, ref: CommentListHandle | null): void => {
			if (!ref) {
				commentListRefs.current
					.delete(postId);
			} else {
				commentListRefs.current
					.set(postId, ref);
			}
		},
		[]
	);

	const focusMobileCommentFormHandler = useCallback(
		(): void => {
			setTimeout(
				(): void => {
					mobileCommentFormRef.current
						?.focusInput();
				},
				0
			);
		},
		[]
	);

	const {
		initMobileMode,
		mobileMode,
		mobileModeChangeHandler
	} = useMobileCommentState(focusMobileCommentFormHandler);

	const closeMobileComment = useCallback(
		(): void => {
			initMobileMode();
			setActivePostId(undefined);
		},
		[initMobileMode]
	);

	const commentCreatedHandler = useCallback(
		(postId: number, commentId: number): void => {
			closeMobileComment();
			commentListRefs.current
				.get(postId)
				?.scrollToComment(commentId);
		},
		[closeMobileComment]
	);

	const commentUpdatedHandler = useCallback(
		(): void => {
			closeMobileComment();
		},
		[closeMobileComment]
	);

	const showCreateMobileCommentHandler = useCallback(
		(postItem: PostListItem): void => {
			const createdBy: UserIdentityModel | null = postItem.createdBy;
			const name: string = isNotNil(createdBy)
				? getComputedDisplayName(createdBy).computedDisplayName
				: localize("_DeletedUser");

			mobileModeChangeHandler({
				kind: "create",
				header: createdBy?.userId === currentUserInfo.userId
					? localize("_ReplyToYourOwnPost")
					: resolveFormattedTextString({
						template: localize("_ReplyToPostOfName"),
						replacements: [
							{
								what: "{{Name}}",
								filler: name
							}
						]
					})
			});

			setActivePostId(postItem.id);
		},
		[
			mobileModeChangeHandler,
			currentUserInfo.userId
		]
	);

	const viewImageByUrlHandler = useCallback(
		(imageUrl: string): void => {
			viewImageHandler({
				url: imageUrl
			});
		},
		[viewImageHandler]
	);

	useEffect(
		(): () => void => {
			if (!isMobile || isNil(activePostId)) {
				onScrollBehaviorChange?.(true);
			} else {
				onScrollBehaviorChange?.(false);
			}

			return (): void => {
				onScrollBehaviorChange?.(true);
			};
		},
		[
			activePostId,
			onScrollBehaviorChange,
			isMobile
		]
	);

	const renderContent = (): ReactElement => {
		if (status === "loaded" && items.length === 0) {
			return (
				<div className={styles.noResultContainer}>
					<NoResults
						trackingId="no-result"
						icon="users"
						title={localize("_NoPostsYet")}
						message={localize("_ThereAreNoPostsWrittenYet")}
						height="fill"
					/>
				</div>
			);
		}

		if (status === "loading" || items.length > 0) {
			return (
				<>
					<ul className={styles.feedList}>
						{items.map(
							(item: PostListItem): ReactElement => (
								<FeedItem
									key={item.id}
									post={item}
									onPostDeleted={postListData.deletePost}
									onNavigateToCommunity={onNavigateToCommunity}
									onOpenPost={onOpenPost}
									onPostUpdate={postListData.updatePost}
									onViewImage={viewImageByUrlHandler}
									options={options}
									isMobile={isMobile}
									onShowCreateMobileComment={showCreateMobileCommentHandler}
									onCommentCreated={commentCreatedHandler}
									onCommentUpdated={commentUpdatedHandler}
									onMobileModeChangeAndFocus={mobileModeChangeHandler}
									onActivePostIdChange={setActivePostId}
									mobileMode={mobileMode}
									currentUserInfo={currentUserInfo}
									setCommentListRef={setCommentListRef}
									timezone={userTimezone}
									alertManager={alertManager}
									onOpenConfirmationDialog={openConfirmationDialog}
								/>
							)
						)}
						<li>
							<LoadMoreIndicator
								isLoadingData={status === "loading"}
								hasMoreItems={hasMore}
								onRequestMoreItems={onLoadMore}
							/>
						</li>
					</ul>
					<MobileCommentBoxPreservedSpace />
					{isMobile && isNotNil(activePostItem) && (
						<FeedMobileComment
							currentUserInfo={currentUserInfo}
							mode={mobileMode}
							ref={mobileCommentFormRef}
							onMobileCommentIsEditingChanged={setIsEditingMobileComment}
							post={activePostItem}
							options={options}
							onCommentUpdated={commentUpdatedHandler}
							onCommentCreated={commentCreatedHandler}
							onClose={closeMobileComment}
							mobileCommentHostingLocation={mobileCommentHostingLocation}
						/>
					)}
					{imageViewerDialog}
				</>
			);
		}

		return (
			<div className={styles.noResultContainer}>
				<NoResults
					trackingId="has-loading-error"
					icon="gear"
					title={localize("_NotAbleToRetrievePosts")}
					message={localize("_WeWereUnableToRetrieveYourPosts")}
					onRetry={onReload}
					height="fill"
				/>
			</div>
		);
	};

	return (
		<>
			{renderContent()}
			{confirmationDialog}
		</>
	);

};
PostList.displayName = "PostList";

type FeedItemProps = {
	post: PostListItem;
	onPostDeleted: (id: number) => void;
	onPostUpdate: (post: PostListItem) => void;
	onNavigateToCommunity?: (communityId: number) => void;
	onOpenPost: (postId: number, deeplink?: "comments") => void;
	onViewImage: (imageUrl: string) => void;
	options: CommunityPostOptionsModel;
	isMobile: boolean;
	onShowCreateMobileComment: (postItem: PostListItem) => void;
	onCommentCreated: (postId: number, commentId: number) => void;
	onCommentUpdated: () => void;
	onMobileModeChangeAndFocus: (mode: MobileCommentMode) => void;
	onActivePostIdChange: (activePostId: number | undefined) => void;
	mobileMode: MobileCommentMode;
	currentUserInfo: UserIdentityModel;
	setCommentListRef: (postId: number, ref: CommentListHandle | null) => void;
	timezone: TimeZone;
	alertManager: AlertManager;
	onOpenConfirmationDialog: (props: ConfirmationDialogInitialData) => Promise<TryResult<void>>;
};

const FeedItem = (props: FeedItemProps): ReactElement => {
	const {
		post,
		onPostDeleted,
		onPostUpdate,
		onViewImage,
		onNavigateToCommunity,
		onOpenPost,
		options,
		isMobile,
		onShowCreateMobileComment,
		onCommentCreated,
		onCommentUpdated,
		onMobileModeChangeAndFocus,
		onActivePostIdChange,
		mobileMode,
		currentUserInfo,
		setCommentListRef,
		timezone,
		alertManager,
		onOpenConfirmationDialog
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const service = useMemo(
		(): HubCommentSectionService => {
			return getCommunityCommentService(
				post.id
			);
		},
		[post.id]
	);

	const { comments } = post;

	const afterUpdateHandler = useCallback(
		(updatedPost: CommunityPostDetailsViewModel): void => {
			onPostUpdate({
				community: updatedPost.community,
				createdBy: updatedPost.createdBy,
				createdOn: updatedPost.createdOn,
				id: updatedPost.id,
				images: updatedPost.images,
				isEditable: updatedPost.isEditable,
				message: updatedPost.message,
				modifiedOn: updatedPost.modifiedOn,
				statistics: updatedPost.statistics,
				comments: post.comments,
				isFollowing: updatedPost.isFollowing,
				hasPoll: updatedPost.hasPoll,
				linkedDocuments: updatedPost.linkedDocuments
			});
		},
		[
			onPostUpdate,
			post
		]
	);

	const navigateToCommunityHandler = useMemo(
		(): (() => void) | undefined => {
			if (isNil(onNavigateToCommunity)) {
				return undefined;
			}

			return (): void => onNavigateToCommunity(
				post.community
					.id
			);
		},
		[
			onNavigateToCommunity,
			post.community
				.id
		]
	);

	const statisticsRetrievedHandler = useCallback(
		(statistics: CommunityPostStatisticsModel): void => {
			onPostUpdate({
				...post,
				statistics
			});
		},
		[
			onPostUpdate,
			post
		]
	);

	const isFollowingChangedHandler = useCallback(
		(isFollowing: boolean): void => {
			onPostUpdate({
				...post,
				isFollowing
			});
		},
		[
			onPostUpdate,
			post
		]
	);

	const openPostHandler = useCallback(
		(deeplink?: "comments"): void => {
			onOpenPost(post.id, deeplink);
		},
		[
			post.id,
			onOpenPost
		]
	);

	const commentCreatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			comments.onNewComment(comment);
			onCommentCreated(post.id, comment.id);
		},
		[
			comments,
			onCommentCreated,
			post.id
		]
	);

	const commentUpdatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			comments.onUpdateComment(comment);
			onCommentUpdated();
		},
		[
			comments,
			onCommentUpdated
		]
	);

	const mobileModeChangeHandler = useCallback(
		(mode: MobileCommentMode): void => {
			onMobileModeChangeAndFocus(mode);
			onActivePostIdChange(post.id);
		},
		[
			onMobileModeChangeAndFocus,
			onActivePostIdChange,
			post.id

		]
	);

	const deletePostHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<void> = await onOpenConfirmationDialog({
				trackingId: "delete-post-confirmation-dialog",
				title: localize("_AreYouSureYouWantDoDeleteThisPost"),
				confirmText: localize("_Delete"),
				cancelText: localize("_Cancel")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunityPost(post.id, globalCancellation);
				onPostDeleted(post.id);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			globalCancellation,
			onOpenConfirmationDialog,
			onPostDeleted,
			post.id
		]
	);

	const hasComments: boolean = post.comments
		.items
		.length > 0
		|| post.comments
			.hasMore;

	return (
		<li
			{...setTrackingId(generateTrackingId("post-list-item", `|${post.id}`))}
			className={styles.postContainerWithSpacing}
		>
			<div className={styles.postContainer}>
				{isBusy && (
					<BusyIndicator />
				)}

				<PostViewer
					onDelete={deletePostHandler}
					onAfterUpdate={afterUpdateHandler}
					onIsFollowingChanged={isFollowingChangedHandler}
					onNavigateToCommunity={navigateToCommunityHandler}
					onStatisticsRetrieved={statisticsRetrievedHandler}
					post={post}
					postOptions={options}
					shouldTruncateMessage
					onOpenPost={openPostHandler}
					onComment={isMobile
						? () => onShowCreateMobileComment(post)
						: undefined
					}
					hasNoBottomMargin={isMobile && !hasComments}
				/>

				<HubCommentSection>
					{!isMobile && (
						<HubCommentSection.CommentForm
							service={service}
							onCreated={commentCreatedHandler}
							currentUserInfo={currentUserInfo}
							options={options}
						/>
					)}
					{hasComments && (
						<HubCommentSection.CommentList
							comments={post.comments.items}
							ref={(x) => setCommentListRef(post.id, x)}
							onStatisticsUpdated={comments.onUpdateCommentStatistics}
							onUpdated={commentUpdatedHandler}
							onCreated={commentCreatedHandler}
							onDeleted={comments.onDeleteComment}
							isMobile={isMobile}
							mobileModeChangeHandler={mobileModeChangeHandler}
							mobileMode={mobileMode}
							service={service}
							currentUserInfo={currentUserInfo}
							timezone={timezone}
							viewImageHandler={onViewImage}
							commentOptions={options}
							renderAfterItems={
								comments.status === "error"
									? (
										<NoResults
											icon="gear"
											title={localize("_NotAbleToRetrieveComments")}
											message={localize("_TheCommentsCouldNotBeRetrieved")}
											onRetry={comments.loadMore}
										/>
									)
									: (
										<HubCommentSection.LoadMore
											hasMore={comments.hasMore}
											isLoading={comments.status === "loading"}
											onLoadMore={comments.loadMore}
											variant="button"
										/>
									)
							}
						/>
					)}
				</HubCommentSection>
			</div>
		</li>
	);
};
FeedItem.displayName = "FeedItem";

type FeedMobileCommentProps = {
	currentUserInfo: UserIdentityModel;
	post: PostListItem;
	onCommentUpdated: () => void;
	onCommentCreated: (postId: number, commentId: number) => void;
	onClose: () => void;
	options: CommunityPostOptionsModel;
	mode: MobileCommentMode;
	onMobileCommentIsEditingChanged: (isEditing: boolean) => void;
	mobileCommentHostingLocation?: MobileCommentHostingLocation;
};

const FeedMobileComment = forwardRef(
	(
		props: FeedMobileCommentProps,
		ref: Ref<MobileCommentFormHandle>
	): ReactElement => {
		const {
			currentUserInfo,
			mode,
			onClose,
			onCommentCreated,
			onCommentUpdated,
			options,
			onMobileCommentIsEditingChanged,
			post,
			mobileCommentHostingLocation = "container"
		} = props;

		const service = useMemo(
			(): HubCommentSectionService => {
				return getCommunityCommentService(post.id);
			},
			[post.id]
		);

		const commentCreatedHandler = useCallback(
			(comment: HubCommentModel): void => {
				post.comments
					.onNewComment(comment);

				onCommentCreated(post.id, comment.id);
			},
			[post, onCommentCreated]
		);

		const commentUpdatedHandler = useCallback(
			(comment: HubCommentModel): void => {
				post.comments
					.onUpdateComment(comment);

				onCommentUpdated();
			},
			[post, onCommentUpdated]
		);

		return (
			<ConditionalWrapper
				condition={mobileCommentHostingLocation === "page"}
				wrapper={children => (
					<Portal container={document.body}>
						<div className={styles.mobileCommentContainerOverlay}>
							<div className={styles.mobileCommentContainer}>
								{children}
								<div className={styles.mobileCommentContainerSafeArea} />
							</div>
						</div>
					</Portal>
				)}
			>
				<MobileComment
					currentUserInfo={currentUserInfo}
					onUpdated={commentUpdatedHandler}
					onCreated={commentCreatedHandler}
					mode={mode}
					onCancel={onClose}
					ref={ref}
					onMobileCommentIsEditingChanged={onMobileCommentIsEditingChanged}
					service={service}
					options={options}
					position={mobileCommentHostingLocation === "page" ? "inline": "bottom"}
				/>
			</ConditionalWrapper>
		);
	}
);
FeedMobileComment.displayName = "FeedMobileComment";

export type MobileCommentHostingLocation = "container" | "page";
